import React from 'react';
import { Printer, Copy, Edit2, RotateCcw, MessageSquare } from 'lucide-react';

import { DemoBanner } from './DemoBanner';

type PrepHighlightsProps = {
  clientName?: string;
};

export function PrepHighlights(_props: PrepHighlightsProps) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <DemoBanner section="Подготовка" />
      <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100">
        <h3 className="text-xl font-semibold text-gray-900">Подготовка к сессии</h3>
        <div className="flex items-center gap-4">
          <button className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
            <Edit2 size={14} /> Редактировать
          </button>
          <button className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
            <Printer size={14} /> Печать
          </button>
          <button className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
            <Copy size={14} /> Копировать
          </button>
        </div>
      </div>

      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-gray-700">
          <span className="text-teal-600 font-semibold">Примечание:</span> Этот отчёт обновляется с каждой новой сессией. Любые ваши правки заблокируют дальнейшие обновления, пока вы не{' '}
          <button className="text-teal-600 underline inline-flex items-center gap-1" type="button">
            <RotateCcw size={12} /> сбросите
          </button>{' '}
          их.
        </p>
      </div>

      <section className="mb-6">
        <h4 className="font-bold text-gray-900 mb-2">Запрос</h4>
        <p className="text-sm text-gray-700">
          Управление устойчивыми чувствами вины и тревоги, улучшение самоотношения
        </p>
      </section>

      <section className="mb-6">
        <h4 className="font-bold text-gray-900 mb-3">План действий клиента</h4>
        <ul className="space-y-2 text-sm text-gray-700">
          {[
            'Практиковать осознавание и называние вины в момент её возникновения;',
            'Использовать технику визуализации для экстернализации вины;',
            'Применять самосострадательные послания при ощущении вины;',
            'Обращаться к доверенным людям в сложные моменты;',
            'Практиковать экспрессивное письмо или рисование для переработки чувств;',
            'Говорить с собой словами мягкости и понимания.',
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-gray-400 mt-0.5">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <button className="mt-3 flex items-center gap-2 text-sm text-teal-600 hover:text-teal-800 transition-colors">
          <MessageSquare size={14} />
          Хотите обсудить, как поддержать эти цели? Спросите ИИ-ассистента
        </button>
      </section>

      <section className="mb-6">
        <h4 className="font-bold text-gray-900 mb-2">Краткое резюме последней сессии</h4>
        <p className="text-sm text-gray-700 mb-3">
          Клиент обсудил прогресс в реализации стратегий самопомощи и решении ситуации с проблемным сотрудником. Выразила смесь гордости и вины за установление границ на работе — опасалась, что была слишком жёсткой.
        </p>
        <p className="text-sm text-gray-700">
          Сессия была посвящена исследованию глубинной вины клиента, которая проявляется в разных сферах жизни — работе, отношениях и самопомощи. Терапевт провёл телесно-ориентированную технику визуализации для работы с виной.
        </p>
      </section>

      <section>
        <h4 className="font-bold text-gray-900 mb-2">Рекомендации к следующей сессии</h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-gray-400">•</span>
            <span>Проверить выполнение домашнего задания по дневнику вины</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">•</span>
            <span>Исследовать связь между перфекционизмом и страхом отвержения</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-gray-400">•</span>
            <span>Продолжить работу с техникой самосострадания</span>
          </li>
        </ul>
      </section>
    </div>
  );
}

